import type { Event as DeviceEvent } from '@eppendorf/vnls-notifications-utils';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { fetcher } from '$shared/fetch';
import {
  REPORTING_SERVICE_BASE_PATH,
  RUN_RECORD_REPORTS_BASE_PATH,
} from '$shared/utils/api-paths';

import type {
  GetRunRecordTelemetryResponse,
  RunRecordReportDetailModel,
} from '$features/report-hub/run-record/run-record-detail.types';

export const runRecordReportQueryKey = 'runRecordReport';
export const runRecordTelemetryQueryKey = 'runRecordTelemetry';
export const runRecordEventsQueryKey = 'runRecordEvents';

/**
 * Loads the full run record detail (metadata + steps + rotor info).
 * Reuses the existing `GET /reporting-service/reports/:id` endpoint which now
 * returns the extended RunRecord payload.
 */
export const createGetRunRecordReportQuery = (reportId: string) => ({
  queryKey: [runRecordReportQueryKey, reportId],
  queryFn: () =>
    fetcher.get<RunRecordReportDetailModel>(`${REPORTING_SERVICE_BASE_PATH}/${reportId}`),
});

export const useGetRunRecordReport = (reportId: string | null) =>
  useQuery<RunRecordReportDetailModel>({
    ...createGetRunRecordReportQuery(reportId ?? ''),
    enabled: Boolean(reportId),
  });

/**
 * Loads the temperature/speed charts for the run record from S3
 * (`reports/{reportId}/history.json`).
 *
 * 404 means the history file hasn't been written yet (telemetry capture during
 * ingestion was skipped or failed). The query exposes the AxiosError so callers
 * can branch on `error.response?.status === 404` and render a "not ready"
 * placeholder instead of a generic error — same pattern as events.
 */
export const useGetRunRecordTelemetry = (reportId: string | null) =>
  useQuery<GetRunRecordTelemetryResponse, AxiosError>({
    queryKey: [runRecordTelemetryQueryKey, reportId],
    queryFn: () =>
      fetcher.get<GetRunRecordTelemetryResponse>(
        `${RUN_RECORD_REPORTS_BASE_PATH}/${reportId}/telemetry`,
      ),
    enabled: Boolean(reportId),
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });

/**
 * Wire format of `GET /reports/run-record/:id/events`. Identical shape to the
 * stability-report events.json that the rest of the report-hub already knows
 * about, so the events sidecar reuses `EventRow` without branching.
 */
interface RawRunRecordEvent {
  message: string;
  timestamp: string;
  tags: string[];
}

const fetchRunRecordEvents = async (reportId: string): Promise<DeviceEvent[]> => {
  const rawEvents = await fetcher.get<RawRunRecordEvent[]>(
    `${RUN_RECORD_REPORTS_BASE_PATH}/${reportId}/events`,
  );

  return rawEvents.map((event, index) => ({
    sourceEventId: `evt-${index}`,
    tenantId: '',
    message: event.message,
    timestamp: event.timestamp,
    subject: { type: 'device', id: '' },
    tags: event.tags,
  }));
};

export const createGetRunRecordEventsQuery = (reportId: string) => ({
  queryKey: [runRecordEventsQueryKey, reportId],
  queryFn: () => fetchRunRecordEvents(reportId),
  enabled: Boolean(reportId),
});

/**
 * Loads events for the run record from S3 (`reports/{reportId}/events.json`).
 *
 * The endpoint returns 404 when the events file hasn't been written yet (CSV
 * upload still pending). The query exposes the AxiosError unchanged so callers
 * can branch on `error.response?.status === 404` and render a "not ready"
 * placeholder instead of a generic error.
 */
export const useGetRunRecordEvents = (reportId: string | null) =>
  useQuery<DeviceEvent[], AxiosError>({
    ...createGetRunRecordEventsQuery(reportId ?? ''),
    enabled: Boolean(reportId),
    // 404 means "not ready yet"; let the component decide how to render it.
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 3;
    },
  });
